import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonList,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close, trash } from 'ionicons/icons';
import { HTMLAttributes, ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  useDeactivateTenantMutation,
  useGetTenantQuery,
} from '../../api/graphql/generated';
import { RightAlignedButtons } from '../../components/button/aligned-buttons';
import { SaveStatusToast } from '../../components/toast/toast.gql';

const DeleteTenantButton = (): ReactElement => {
  const [
    deactivateTenant,
    { loading, error: deactivateError, data: deactivateData },
  ] = useDeactivateTenantMutation();

  const error = deactivateData?.deactivateTenant === false || deactivateError;

  const [modalVisisble, setModalVisisble] = useState(false);
  const { i18n } = useTranslation();

  const showModal = () => {
    setModalVisisble(true);
  };

  const dismissModal = () => {
    setModalVisisble(false);
  };

  return (
    <>
      <IonButton onClick={showModal}>
        <IonIcon icon={trash} color="danger" />
      </IonButton>
      <IonModal isOpen={modalVisisble} onDidDismiss={dismissModal}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot={'start'} onClick={dismissModal}>
              <IonButton>
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonTitle>{i18n.t('common:tenant_deactivation_header')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <DeleteTenantConfirmationContent
          onConfirm={async () => {
            dismissModal();
            await deactivateTenant();
          }}
        />
      </IonModal>
      <SaveStatusToast
        loading={loading}
        error={error}
        defaultErrorMessageKey={'common:tenant_deactivation_failed'}
        showOnly="error"
      />
    </>
  );
};

interface DeleteTenantConfirmationContentProps {
  onConfirm: () => void;
}

const DeleteTenantConfirmationContent = (
  props: DeleteTenantConfirmationContentProps
) => {
  const { onConfirm } = props;
  const { i18n } = useTranslation();
  const { data: tenantData } = useGetTenantQuery({
    fetchPolicy: 'cache-only',
  });

  const tenant = tenantData?.getTenant;

  const organizationName = tenant?.name ?? i18n.t('common:unnamed');
  const description = i18n.t('common:tenant_deactivate_description', {
    organizationName,
  });
  const deleteText = String(i18n.t('common:delete')).toLowerCase();
  const confirmation = i18n.t('common:tenant_deactivation_confirmation');

  const [enableConfirmButton, setEnableConfirmButton] = useState(false);

  return (
    <>
      <IonContent className="ion-padding" scrollY={false}>
        <div>
          <p>
            <Trans>{description}</Trans>
          </p>
          <p>
            <Trans>{confirmation}</Trans>
          </p>
          <IonList>
            <IonItem lines="full">
              <ConfirmInput
                text={deleteText}
                onInput={(matched) => setEnableConfirmButton(matched)}
                inputProps={{
                  placeholder: i18n.t('common:deleting_organization', {
                    organizationName,
                  }),
                }}
              />
            </IonItem>
          </IonList>
        </div>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <RightAlignedButtons>
            <IonButton
              color="danger"
              disabled={!enableConfirmButton}
              onClick={onConfirm}
            >
              {i18n.t('common:delete')}
            </IonButton>
          </RightAlignedButtons>
        </IonToolbar>
      </IonFooter>
    </>
  );
};

interface ConfirmInputProps {
  text: string;
  onInput: (matched: boolean) => void;
  inputProps?: HTMLAttributes<HTMLIonInputElement>;
}

const ConfirmInput = (props: ConfirmInputProps): ReactElement => {
  const { text, onInput, inputProps } = props;

  const handleInput = (e: CustomEvent) => {
    onInput(e.detail.value === text);
  };

  return <IonInput {...inputProps} onIonInput={handleInput} />;
};

export { DeleteTenantButton };
